import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import usePaginationParams from '@hooks/usePaginationParams';

import type {
  MyDispositionsProviderType,
  MyDispositionsProviderProps,
  MyDispositionType,
} from './types';

const Context = createContext({} as MyDispositionsProviderType);
const useMyDispositionsContext = (): MyDispositionsProviderType => useContext(Context);

const MyDispositionsProvider = ({ children }: MyDispositionsProviderProps) => {
  const { executionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [myDispositionsList, setMyDispositionsList] = useState<MyDispositionType[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const getMyDispositionsList = useCallback(async ({ ...filters }) => {
    setLoading(true);
    try {
      const response = await api.get(`/campaign_execution/${executionId}/my_dispositions`, {
        params: {
          pageIndex: filters.page || 1,
          pageLimit: filters.limit || 10,
          ...filters,
        },
      });
      setMyDispositionsList(response.data.data);
      setTotalPages(response.data.total);
    } catch (error) {
      toast.error('Erro ao carregar os atendimentos. Tente novamente mais tarde.');
      datadogRum.addError(new Error('Fail to get my dispositions list'), {
        error,
        executionId,
        filters,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  usePaginationParams({ onParamsChange: getMyDispositionsList });

  const value = useMemo(() => ({
    getMyDispositionsList,
    myDispositionsList,
    totalPages,
    loading,
    showFilters,
    setShowFilters,
  }), [
    getMyDispositionsList, myDispositionsList,
    totalPages, loading, showFilters,
  ]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export { MyDispositionsProvider, useMyDispositionsContext };
